import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Paper,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  useTheme,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  backendFieldsMap,
  updateFromFields,
  updateToFields,
} from "./massUpdateFieldConfig";
import { getService } from "../../../reducers/service";
import { serviceNames } from "../../../utils/constants";
import { notify } from "../../../reducers/NotifierReducer";
import { setCircularLoading } from "../../../reducers/UiReducer";
import { RegularExpressions } from "../../../utils/regular-expressions";

const MassUpdate = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [courseNumberOptions, setCourseNumberOptions] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [courseLocationOptions, setCourseLocationOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const [activeField, setActiveField] = useState(null);
  const [selectedDropdown, setSelectedDropdown] = useState({});
  const [textFieldValues, setTextFieldValues] = useState({});

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const handleOpenDialog = () => {
    const selectedField = Object.keys(selectedDropdown)[0];

    if (!selectedField) {
      dispatch(notify("Please select at least one field", "warning"));
      return;
    }

    setOpenConfirmDialog(true);
  };

  const handleCloseDialog = () => setOpenConfirmDialog(false);

  const fetchDropdownValues = async () => {
    const services = {
      distinct: getService(serviceNames.trainingCourseDistinct),
    };

    const [distinctResult] = await Promise.all([
      services.distinct.find({
        query: { column: ["Course#", "CourseName", "Category", "Location"] },
      }),
    ]);

    setCourseNumberOptions(
      distinctResult["Course#"]?.map((courseNum) => ({
        label: courseNum,
        value: courseNum,
      })) || []
    );
    setCourseOptions(
      distinctResult?.CourseName.map((courseName) => ({
        label: courseName,
        value: courseName,
      })) || []
    );
    setCategoryOptions(
      distinctResult?.Category.map((category) => ({
        label: category,
        value: category,
      })) || []
    );
    setCourseLocationOptions(
      distinctResult?.Location.map((location) => ({
        label: location,
        value: location,
      })) || []
    );
  };

  useEffect(() => {
    fetchDropdownValues();
  }, []);

  const handleDropdownChange = (fieldName, value) => {
    setSelectedDropdown({ [fieldName]: value });
    setTextFieldValues({ [fieldName]: "" });
    setActiveField(fieldName);
  };

  const handleTextFieldChange = (e, fieldName) => {
    setTextFieldValues((prev) => ({
      ...prev,
      [fieldName]: e.target.value,
    }));
  };

  const renderField = (field) => {
    const { type, label, field: fieldName, options = [] } = field;
    const isActive = activeField === fieldName;

    if (type === "autocomplete") {
      return (
        <Autocomplete
          options={options}
          value={selectedDropdown[fieldName] || null}
          onChange={(_, value) => handleDropdownChange(fieldName, value)}
          getOptionLabel={(option) => option?.label || ""}
          isOptionEqualToValue={(o, v) => o?.value === v?.value}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              size="small"
              margin="normal"
              fullWidth
            />
          )}
        />
      );
    }

    if (type === "text") {
      return (
        <TextField
          key={fieldName}
          label={label}
          value={textFieldValues[fieldName] || ""}
          onChange={(e) => handleTextFieldChange(e, fieldName)}
          fullWidth
          variant="outlined"
          size="small"
          margin="normal"
          disabled={!isActive}
        />
      );
    }

    return null;
  };

  const getOptions = (field) => {
    switch (field.field) {
      case "Course#":
        return { ...field, options: courseNumberOptions };
      case "courseName":
        return { ...field, options: courseOptions };
      case "category":
        return { ...field, options: categoryOptions };
      case "courseLocation":
        return { ...field, options: courseLocationOptions };
      default:
        return field;
    }
  };

  const handleConfirm = async () => {
    dispatch(setCircularLoading(true));
    try {
      const field = Object.keys(selectedDropdown)[0];
      const fromValue = selectedDropdown[field]?.value;
      const toValue = textFieldValues[field];

      if (!field || !fromValue || !toValue) {
        dispatch(notify("Please select a valid value to update", "warning"));
        return;
      }

      if (!RegularExpressions.Name.test(toValue)) {
        dispatch(notify("Invalid value for the target field", "warning"));
        return;
      }

      if (field === "Course#") {
        const existingCourseNumbers = courseNumberOptions.map(
          (opt) => opt.value
        );
        if (existingCourseNumbers.includes(toValue)) {
          dispatch(
            notify(`Course Number "${toValue}" already exists.`, "warning")
          );
          return;
        }
      }

      const trainingCourseField =
        backendFieldsMap[field]?.trainingCourse || field;
      const perTrainingField = backendFieldsMap[field]?.perTraining || field;

      const trainingCourseQuery = {
        [trainingCourseField]: fromValue,
        $limit: -1,
      };
      const perTrainingQuery = { [perTrainingField]: fromValue, $limit: -1 };
      const trainingCourseService = getService(serviceNames.trainingCourse);
      const perTrainingService = getService(serviceNames.perTraining);

      const [trainingCourseResponse, perTrainingResponse] = await Promise.all([
        trainingCourseService.find({
          query: trainingCourseQuery,
        }),
        perTrainingService.find({ query: perTrainingQuery }),
      ]);

      const trainingData = trainingCourseResponse || [];
      const perTrainingData = perTrainingResponse || [];

      await Promise.all(
        trainingData.map((record) =>
          record.id
            ? trainingCourseService.patch(record.id, {
                [trainingCourseField]: toValue,
              })
            : Promise.resolve()
        )
      );

      await Promise.all(
        perTrainingData.map((record) =>
          record.id
            ? perTrainingService.patch(record.id, {
                [perTrainingField]: toValue,
              })
            : Promise.resolve()
        )
      );

      setSelectedDropdown({});
      setTextFieldValues({});
      setActiveField(null);
      fetchDropdownValues();
      dispatch(notify("Mass update successful!", "success"));
    } catch (error) {
      dispatch(notify("Mass update failed", "error"));
    } finally {
      dispatch(setCircularLoading(false));
      setOpenConfirmDialog(false);
    }
  };

  const fromFieldConfigs = updateFromFields.map(getOptions);
  const toFieldConfigs = updateToFields.map(getOptions);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <Paper
        sx={{
          padding: theme.spacing(4),
          backgroundColor: theme.palette.background.paper,
          width: "100%",
          maxWidth: 1000,
        }}
      >
        <Box sx={{ height: 420, overflow: "auto" }}>
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: 30,
              display: "flex",
              justifyContent: "center",
            }}
          >
            Mass Update Training Records
          </Typography>
          <Grid container spacing={4} sx={{ marginTop: 1 }}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: "bold", fontSize: 19 }}
              >
                Update From
              </Typography>
              <Grid container spacing={2}>
                {fromFieldConfigs.map((field) => (
                  <Grid item xs={12} key={field.field}>
                    {renderField(field)}
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: "bold", fontSize: 19 }}
              >
                Update To
              </Typography>
              <Grid container spacing={2}>
                {toFieldConfigs.map((field) => (
                  <Grid item xs={12} key={field.field}>
                    {renderField(field)}
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>

        <Box mt={4} display="flex" justifyContent="center">
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenDialog}
          >
            Update
          </Button>
        </Box>
      </Paper>

      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirm Mass Update</DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontWeight: "semibold",
              fontSize: 16,
              color: "red",
              margin: 1,
            }}
          >
            Are you sure you want to conduct this mass update? All records with
            the criteria showing in the Update From column will be updated to
            what is shown in the Update To column.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseDialog()}>No</Button>
          <Button
            onClick={() => handleConfirm()}
            variant="contained"
            color="primary"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MassUpdate;
